import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { authPaths } from '@/modules/auth/routes.tsx';
import {
  APPLY_REGISTER_CLASS_ID,
  APPLY_REGISTER_ID,
  REGISTER_ID_SEARCH_PARAM_KEY,
} from '@/modules/common/constants/search-params.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import {
  useCurrentUserRegistersQuery,
  useRegisterClassListInfinityQuery,
} from '@/modules/investor-dashboard/queries/registers.tsx';
import {
  Alert,
  Button,
  CustomCheckbox,
  ModalContentWrapper,
  SelectPaginate,
  SelectPaginateOption,
  SelectWithCreatable,
  SelectWithCreatableOption,
  TooltipNew,
} from 'ui';
import { AlertOrangeIcon, CopyIcon, InfoCircleBlueIcon } from 'ui/icons';
import { copyTextToClipBoard } from 'ui/utils';
import { UserRegisterResponse } from 'api';

interface RegisterClassSelectPaginate {
  id: number;
  current_price_per_share: number;
  toString: () => string;
}

interface RegisterSelectPaginate {
  id: number;
  toString: () => string;
}

export interface IssuerInviteShareholderModalProps {
  open: boolean;
  onClose: () => void;
  defaultRegisterOption?: SelectWithCreatableOption<RegisterSelectPaginate>;
  defaultRegisterClassOption?: SelectPaginateOption<RegisterClassSelectPaginate>;
  disabledSubscription?: boolean;
}

export const IssuerInviteShareholderModal = ({
  open,
  defaultRegisterOption,
  defaultRegisterClassOption,
  disabledSubscription = false,
  onClose,
}: IssuerInviteShareholderModalProps) => {
  const [params] = useSearchParams();
  const registerId = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  const [isAddSubscription, setIsAddSubscription] = useState(
    disabledSubscription ? false : !!defaultRegisterClassOption,
  );
  const getRegisterTypeStrings = useRegisterTypeStrings();

  const { data: registerList } = useCurrentUserRegistersQuery();

  const registerOptions = useMemo<SelectWithCreatableOption<UserRegisterResponse>[]>(
    () =>
      registerList?.data?.map<SelectWithCreatableOption<UserRegisterResponse>>(item => ({
        value: item,
        label: item.register_obj?.name || '',
      })) || [],
    [registerList],
  );

  const [register, setRegister] = useState<
    SelectWithCreatableOption<UserRegisterResponse> | undefined
  >(
    registerOptions.find(
      item =>
        item.value?.register_obj?.id === (defaultRegisterOption?.value.id || +(registerId || 0)),
    ),
  );

  const [lastRegisterClass, setLastRegisterClass] = useState<
    SelectPaginateOption<RegisterClassSelectPaginate> | undefined
  >(defaultRegisterClassOption);

  const [registerClass, setRegisterClass] = useState<
    SelectPaginateOption<RegisterClassSelectPaginate> | undefined
  >(defaultRegisterClassOption);

  const inviteLink = useMemo(
    () =>
      `${location.origin}${authPaths.static.invitation}?${APPLY_REGISTER_ID}=${
        register?.value?.register_obj?.private_id
      }${
        isAddSubscription && registerClass?.value?.id
          ? `&${APPLY_REGISTER_CLASS_ID}=${registerClass.value.id}`
          : ''
      }`,
    [isAddSubscription, register?.value?.register_obj?.private_id, registerClass?.value?.id],
  );

  const { fetchNextPage: fetchNextRegisterClassListPage } = useRegisterClassListInfinityQuery({
    page: 1,
    page_size: 30,
    register_id: register?.value?.register_id,
  });

  const handleAsyncRegisterClassList = useCallback(
    async (currentPage: number, search?: string) => {
      const response = await fetchNextRegisterClassListPage({
        pageParam: {
          page: currentPage,
          search,
        },
      });

      const options: SelectPaginateOption<RegisterClassSelectPaginate>[] =
        response.data?.pages.reduce((acc: any, { data, page }) => {
          if (page !== currentPage) {
            return acc;
          }

          return data.map(({ class_name, id, current_price_per_share }) => ({
            label: class_name,
            value: { id, current_price_per_share, toString: () => class_name },
          }));
        }, []) || [];
      return options;
    },
    [fetchNextRegisterClassListPage],
  );

  const handleToggleSubscription = useCallback(
    (check: boolean) => {
      if (check) {
        setRegisterClass(lastRegisterClass);
      } else {
        setLastRegisterClass(registerClass);
        setRegisterClass(undefined);
      }

      setIsAddSubscription(check);
    },
    [lastRegisterClass, registerClass],
  );

  const handleChangeRegister = useCallback(
    (option: SelectWithCreatableOption<UserRegisterResponse> | null) => {
      if (!option?.value || option?.value?.id === register?.value?.id) return;

      setRegisterClass(undefined);
      setLastRegisterClass(undefined);
      setRegister(option);
    },
    [register?.value?.id],
  );

  return (
    <ModalContentWrapper
      open={open}
      title={`Generate a ${getRegisterTypeStrings('application', {
        registerId: +(registerId || 0),
      })} link`}
      onClose={onClose}
    >
      <div className="flex flex-col gap-8 min-h-full justify-start">
        <div className="w-fit">
          <TooltipNew
            text={`You can't subscribe to a class with a zero ${getRegisterTypeStrings('nav')}.`}
            disabled={!disabledSubscription}
          >
            <CustomCheckbox
              label={`Add ${getRegisterTypeStrings('application', {
                registerId: +(registerId || 0),
              })}`}
              checked={isAddSubscription}
              onChange={e => handleToggleSubscription(e.target.checked)}
              disabled={disabledSubscription}
            />
          </TooltipNew>
        </div>
        <Alert
          variant="neutral-2"
          icon={InfoCircleBlueIcon}
          label={
            'This link can either be used by new investors to sign up their user account, KYC and subscribe/apply for units/shares first time ' +
            'or by existing investors to log in and subscribe for more unit/shares.'
          }
          className="!mb-0"
        />
        <SelectWithCreatable<UserRegisterResponse, string>
          label="register"
          options={registerOptions}
          value={register}
          onChange={handleChangeRegister}
        />
        {isAddSubscription && (
          <SelectPaginate<RegisterClassSelectPaginate>
            cacheUniqs={[
              'register',
              register?.value?.register_id,
              registerId,
              defaultRegisterOption?.value?.id,
            ]}
            label="class"
            onAsync={handleAsyncRegisterClassList}
            value={registerClass}
            defaultValue={registerClass}
            onChange={option => {
              if (option?.value?.current_price_per_share === 0) {
                toast(
                  () => (
                    <span className="flex flex-col justify-center items-center gap-2 text-sm">
                      <span>
                        <img src={AlertOrangeIcon} className="w-8 h-8" alt="alert" />
                      </span>
                      <div>
                        You can't subscribe to a class with a zero {getRegisterTypeStrings('nav')}.
                      </div>
                    </span>
                  ),
                  {
                    duration: 5000,
                    id: 'reset',
                  },
                );
                return;
              }
              if (option) {
                setRegisterClass(option);
                setLastRegisterClass(option);
              }
            }}
          />
        )}
        <div className="w-full flex flex-col justify-start">
          <p className="text-[10px] leading-[18px] opacity-64 uppercase">Invite link</p>
          <p className="text-[18px] leading-[26px] break-all">{inviteLink}</p>
        </div>
        <Button
          variant="secondary"
          icon={CopyIcon}
          onClick={() =>
            copyTextToClipBoard(inviteLink, {
              successMessage: 'Invite link copied successfully',
            })
          }
        >
          Copy link
        </Button>
      </div>
    </ModalContentWrapper>
  );
};
